<div class="sidebar flex h-full flex-shrink-0 select-none relative">
  <!-- ═══════════════════════════════════════════════════════════
       PRIMARY NAVIGATION RAIL (icon rail — expands to show labels)
  ═══════════════════════════════════════════════════════════ -->
  <div
    class="sidebar-primary-rail flex flex-col flex-shrink-0 h-full transition-all duration-300 ease-in-out"
    [style.width]="isCollapsed() ? '72px' : '220px'"
  >
    <!-- Logo -->
    <div
      class="flex-shrink-0 flex items-center pt-4 pb-3 px-2"
      [class.justify-center]="isCollapsed()"
      [class.justify-between]="!isCollapsed()"
    >
      <button
        type="button"
        class="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm transition-transform hover:scale-[1.02] cursor-pointer"
        style="background: var(--primary)"
        [title]="isCollapsed() ? 'Expand sidebar' : 'IDP'"
        (click)="isCollapsed() ? toggleCollapse.emit() : null"
      >
        IDP
      </button>
      @if (!isMobile() && !isCollapsed()) {
        <button
          type="button"
          class="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:opacity-85 cursor-pointer sidebar-rail-btn"
          title="Collapse sidebar"
          (click)="toggleCollapse.emit()"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            stroke-width="2.5"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      }
    </div>

    <!-- Primary nav -->
    <div
      class="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2"
      [class.items-center]="isCollapsed()"
    >
      @for (item of primaryItems; track item.id) {
        <button
          type="button"
          class="sidebar-rail-btn relative flex items-center rounded-xl text-left cursor-pointer"
          [class.sidebar-rail-btn--active]="activePrimary() === item.id"
          [class.w-11]="isCollapsed()"
          [class.h-11]="isCollapsed()"
          [class.justify-center]="isCollapsed()"
          [class.w-full]="!isCollapsed()"
          [class.gap-3]="!isCollapsed()"
          [class.px-3]="!isCollapsed()"
          [class.py-2.5]="!isCollapsed()"
          [title]="item.label"
          (click)="selectPrimary(item)"
        >
          <svg
            class="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            stroke-width="1.75"
          >
            <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="item.icon" />
          </svg>

          <span
            class="sidebar-item-label text-[13px] leading-tight truncate flex-1 min-w-0"
            [class.sidebar-item-label-collapsed]="isCollapsed()"
            [style.font-weight]="activePrimary() === item.id ? '600' : '500'"
          >{{ item.label }}</span>
        </button>
      }
    </div>

    <!-- Bottom: profile + logout -->
    <div
      class="flex flex-col gap-1.5 pb-4 pt-3 border-t flex-shrink-0 px-2"
      style="border-color: var(--sidebar-border)"
      [class.items-center]="isCollapsed()"
    >
      <button
        type="button"
        class="sidebar-rail-btn flex items-center rounded-xl cursor-pointer"
        [class.sidebar-rail-btn--active]="activePrimary() === 'profile'"
        [class.w-11]="isCollapsed()"
        [class.h-11]="isCollapsed()"
        [class.justify-center]="isCollapsed()"
        [class.w-full]="!isCollapsed()"
        [class.gap-3]="!isCollapsed()"
        [class.px-3]="!isCollapsed()"
        [class.py-2]="!isCollapsed()"
        title="My Profile"
        (click)="selectBottom(bottomItems[0])"
      >
        <div
          class="w-9 h-9 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
          style="background: var(--primary)"
        >
          {{ userInitials() }}
        </div>
        @if (!isCollapsed()) {
          <span class="text-[13px] truncate flex-1 min-w-0" style="color: var(--sidebar-text)">
            {{ userDisplayName() }}
          </span>
        }
      </button>

      <button
        type="button"
        class="sidebar-rail-btn flex items-center rounded-xl cursor-pointer"
        [class.w-11]="isCollapsed()"
        [class.h-11]="isCollapsed()"
        [class.justify-center]="isCollapsed()"
        [class.w-full]="!isCollapsed()"
        [class.gap-3]="!isCollapsed()"
        [class.px-3]="!isCollapsed()"
        [class.py-2.5]="!isCollapsed()"
        title="Sign out"
        (click)="logout()"
      >
        <svg
          class="w-5 h-5 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          stroke-width="1.75"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1"
          />
        </svg>
        @if (!isCollapsed()) {
          <span class="text-[13px] font-medium" style="color: var(--sidebar-text)">Sign out</span>
        }
      </button>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════
       SECONDARY  PANEL  (collapsible, ~198 px)
  ═══════════════════════════════════════════════════════════ -->
  @if (canShowSecondaryNav()) {
    <div
      class="flex flex-col h-full transition-all duration-300 ease-in-out overflow-hidden"
      [style.width]="canRenderSecondaryPanel() ? '198px' : '0px'"
      [style.opacity]="canRenderSecondaryPanel() ? '1' : '0'"
      [style.border-right]="canRenderSecondaryPanel() ? '1px solid var(--sidebar-border)' : '0px solid transparent'"
      [style.pointer-events]="canRenderSecondaryPanel() ? 'auto' : 'none'"
    >
      <!-- Panel header -->
      <div
        class="flex items-center justify-between px-3 py-3 flex-shrink-0"
        style="border-bottom: 1px solid var(--sidebar-border)"
      >
        <span class="font-semibold text-sm" style="color: var(--sidebar-text-hover)">
          {{ secondaryTitle() }}
        </span>
        <button
          class="w-6 h-6 rounded-lg flex items-center justify-center transition-all hover:opacity-80"
          style="background: var(--sidebar-active-bg); color: var(--sidebar-active-text)"
          (click)="secondaryVisible.set(false)"
          title="Collapse"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            stroke-width="2.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M11 17l-5-5 5-5M18 17l-5-5 5-5"
            />
          </svg>
        </button>
      </div>

      <!-- Nav list -->
      <nav class="flex-1 overflow-y-auto overflow-x-hidden py-2 space-y-0.5 pr-1">
        @for (item of activeSecondaryItems(); track item.id) {
          <!-- ── Section (has children) ── -->
          @if (item.children && item.children.length > 0) {
            @let sectionActive = isSectionActive(item);
            <div class="mb-0.5">
              <button
                class="nav-item w-full flex items-center gap-2 py-2 rounded-lg text-left relative transition-all"
                [style.padding-left]="'10px'"
                [style.padding-right]="'8px'"
                [style.margin-left]="sectionActive ? '6px' : '4px'"
                [style.margin-right]="sectionActive ? '6px' : '4px'"
                [style.width]="sectionActive ? 'calc(100% - 12px)' : 'calc(100% - 8px)'"
                [class.active]="sectionActive"
                (click)="toggleSection(item.id)"
              >
                <!-- Active left accent bar -->
                @if (sectionActive) {
                  <span
                    class="absolute left-0 top-2 bottom-2 w-0.5 rounded-r-full"
                    style="background: var(--sidebar-active-border)"
                  ></span>
                }

                <svg
                  class="w-3.5 h-3.5 flex-shrink-0 transition-colors"
                  [style.color]="sectionActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-group-text)'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="item.icon" />
                </svg>

                <span
                  class="flex-1 text-[11px] font-bold uppercase tracking-wide truncate transition-colors"
                  [style.color]="sectionActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-group-text)'"
                  style="letter-spacing: 0.6px"
                >
                  {{ item.label }}
                </span>

                <svg
                  class="w-3 h-3 flex-shrink-0 transition-all duration-200"
                  [style.transform]="isExpanded(item.id) ? 'rotate(90deg)' : 'rotate(0deg)'"
                  [style.color]="sectionActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-group-text)'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  stroke-width="2.5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <!-- Children -->
              @if (isExpanded(item.id)) {
                <div class="fade-in mt-0.5 mb-1 space-y-0.5">
                  @for (child of item.children; track child.id) {
                    @let childActive = !!child.route && isActive(child.route);
                    <button
                      class="nav-item w-full flex items-center gap-2 py-1.5 rounded-lg text-left relative transition-all"
                      [style.padding-left]="'28px'"
                      [style.padding-right]="'8px'"
                      [style.margin-left]="childActive ? '8px' : '4px'"
                      [style.margin-right]="childActive ? '8px' : '4px'"
                      [style.width]="childActive ? 'calc(100% - 16px)' : 'calc(100% - 8px)'"
                      [class.active]="childActive"
                      (click)="navigate(child)"
                    >
                      <!-- Active left accent bar -->
                      @if (childActive) {
                        <span
                          class="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full"
                          style="background: var(--sidebar-active-border)"
                        ></span>
                      }

                      <!-- Bullet dot -->
                      <span
                        class="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all"
                        [style.background]="childActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-group-text)'"
                        [style.width]="childActive ? '6px' : '5px'"
                        [style.height]="childActive ? '6px' : '5px'"
                      ></span>

                      <span
                        class="flex-1 text-[13px] truncate transition-colors"
                        [style.font-weight]="childActive ? '600' : '400'"
                      >{{ child.label }}</span>

                      @if (childActive) {
                        <svg
                          class="w-2.5 h-2.5 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          stroke-width="2.5"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      }
                    </button>
                  }
                </div>
              }
            </div>

            <!-- ── Leaf item (no children) ── -->
          } @else {
            @let leafActive = !!item.route && isActive(item.route);
            <button
              class="nav-item flex items-center gap-2.5 px-3 py-2 rounded-lg relative transition-all"
              [style.margin-left]="leafActive ? '6px' : '4px'"
              [style.margin-right]="leafActive ? '6px' : '4px'"
              [style.width]="leafActive ? 'calc(100% - 12px)' : 'calc(100% - 8px)'"
              [class.active]="leafActive"
              (click)="navigate(item)"
            >
              <!-- Active left accent bar -->
              @if (leafActive) {
                <span
                  class="absolute left-0 top-2 bottom-2 w-0.5 rounded-r-full"
                  style="background: var(--sidebar-active-border)"
                ></span>
              }
              <svg
                class="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                stroke-width="1.7"
              >
                <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="item.icon" />
              </svg>
              <span class="flex-1 text-[13px] truncate" [style.font-weight]="leafActive ? '600' : '400'">{{ item.label }}</span>
              @if (item.badge) {
                <span class="badge-premium flex-shrink-0">{{ item.badge }}</span>
              }
            </button>
          }
        }
      </nav>

      <!-- Footer -->
      <div
        class="flex-shrink-0 px-3 py-2.5 flex items-center gap-2"
        style="border-top: 1px solid var(--sidebar-border)"
      >
        <div
          class="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          style="background: var(--primary)"
        >
          {{ userInitials() }}
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-xs font-semibold truncate" style="color: var(--sidebar-text-hover)">
            {{ userDisplayName() }}
          </div>
          <div class="text-[10px] truncate" style="color: var(--sidebar-group-text)">
            {{ userEmail() }}
          </div>
        </div>
        <button
          class="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-all hover:opacity-80"
          style="color: var(--sidebar-group-text)"
          title="Sign out"
          (click)="logout()"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1"/>
          </svg>
        </button>
      </div>
    </div>
  }

  <!-- When secondary is collapsed: show expand button on primary rail -->
  @if (!secondaryVisible() && canShowSecondaryNav() && !isCollapsed()) {
    <button
      class="absolute top-1/2 -translate-y-1/2 w-5 h-10 rounded-r-lg flex items-center justify-center z-20 shadow"
      [style.left]="isCollapsed() ? '72px' : '220px'"
      style="background: var(--sidebar-active-bg); color: var(--sidebar-active-text)"
      (click)="secondaryVisible.set(true)"
      title="Expand"
    >
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5-5 5M6 7l5 5-5 5" />
      </svg>
    </button>
  }
</div>
